import { useEffect, useState } from "react";
import { SectionId } from "@/core/global/SectionRegistry";
import { TileStatus } from "@/components/EnrollmentTile";
import { User } from "@/core/models/global/User";
import { enrollmentApi, housingApi, dataApi } from "@/services/api";
import { useDocumentStatusPush } from "@/hooks/useDocumentStatusPush";
import { mapUserToForms, getEffectiveMilitaryInfo } from "@/core/mappers/userMapper";
import { PAGE_IDS } from "@/core/constants/pageIds";
import { MilitaryDocumentsStatus, DocumentUploadStatus } from "@/core/models/global/DocumentUploadStatus";
import { getMilitaryDocumentRequirements } from "@/components/militaryVeteransPage/militaryDocumentRules";

export type DynamicTileState = {
    status: TileStatus;
    // When true, the tile renders in EnrollmentTile's disabled state (dimmed, non-navigable).
    // Omitted/undefined means "no override" - the tile falls back to the registry's enabled flag.
    disabled?: boolean;
    // When true, the tile is removed from the dashboard entirely (Dashboard.tsx filters it out).
    // Omitted/undefined means "no override" - the tile falls back to the registry's hidden flag.
    hidden?: boolean;
};

export type DynamicTileStatusMap = Partial<Record<SectionId, DynamicTileState>>;

// Single place for dashboard tiles. 
// Dashboard.tsx looks up `dynamicStatuses[section.id]` for every tile and falls back to the static placeholder when a section has no entry here. 
// Adding new tiles never requires touching Dashboard.tsx render/JSX - only adding a case here.
// `refreshKey` re-reads every status when it changes (the sidebar passes the route, so a status that changed while the
// applicant worked is picked up on their next navigation). The dashboard omits it and reads once.
export function useDynamicTileStatuses(user: User | null, refreshKey: string | number = 0): DynamicTileStatusMap {
    // A document's PandaDoc status changed in the background (pushed over SignalR): read every status again.
    const [pushCount, setPushCount] = useState(0);
    useDocumentStatusPush(() => setPushCount((n) => n + 1));
    const tileKey = `${refreshKey}:${pushCount}`;

    return {
        [SectionId.ApplicationFees]: useFeesStatus(user, tileKey),
        [SectionId.HousingApplication]: useHousingStatus(user, tileKey),
        [SectionId.VeteranBenefits]: useVeteranBenefitsStatus(user, tileKey),
        [SectionId.TechnologyCheck]: { status: useTechnologyCheckStatus(user, tileKey) },
        [SectionId.FinancialAid]: useFinancialAssistanceStatus(user, tileKey),
        [SectionId.TransferCredits]: { status: useTransferCreditsStatus(user, tileKey) },
        [SectionId.HighSchoolTranscript]: { status: useHighSchoolTranscriptStatus(user, tileKey) },
    };
}

// High School Transcript tile status (UAT SEP-108: the tile had no status source, so it always read Not Started). The
// same milestones as the High School Transcript page's tracker, plus the student's own step:
//   complete   <- the official transcript is on file
//   inReview   <- the student signed the transcript request (done with the Personal Profile): "In Progress" badge, "Review" button
//   pending    <- a staff milestone (request sent to the school / transcript received) is recorded
//   notStarted <- otherwise
function useHighSchoolTranscriptStatus(user: User | null, refreshKey: string | number = 0): TileStatus {
    const [status, setStatus] = useState<TileStatus>("notStarted");

    useEffect(() => {
        let cancelled = false;
        const applicantId = user?.applicantId;
        if (!applicantId) return;

        Promise.all([
            dataApi.getHighSchoolTranscriptStatus(applicantId).catch(() => null),
            enrollmentApi.getTranscriptStatus(applicantId).catch(() => null),
        ]).then(([tracker, request]) => {
            if (cancelled) return;
            if (tracker?.hasTranscript) setStatus("complete");
            else if (request?.signed) setStatus("inReview");
            else if (tracker?.dateRecd != null || tracker?.dateReq != null) setStatus("pending");
            else setStatus("notStarted");
        });

        return () => {
            cancelled = true;
        };
    }, [user?.applicantId, refreshKey]);

    return status;
}

// Transfer Credits tile status. Sourced from the server-side Applicant.DashboardStatus_VW
// (ApplicantDashboardStatusVw.TransferCreditStatus), which resolves to:
//   "In Progress" => any of the applicant's ApplicantPostSecondary rows has a
//                    TranscriptOrderStatus set or an UnofficialTranscriptApplicantDocumentId
//   "Not Started" => none do
// The view has no "completed" state for this tile, so it only maps to pending / notStarted.
function useTransferCreditsStatus(user: User | null, refreshKey: string | number = 0): TileStatus {
    const [status, setStatus] = useState<TileStatus>("notStarted");

    useEffect(() => {
        let cancelled = false;

        if (!user?.applicantId) return;

        enrollmentApi
            .getTransferCreditsStatus()
            .then((raw) => {
                if (cancelled) return;
                setStatus(mapTransferCreditsStatus(raw));
            })
            .catch(() => {
                // No row yet / lookup failed — leave the tile in its default state.
                if (!cancelled) setStatus("notStarted");
            });

        return () => {
            cancelled = true;
        };
    }, [user?.applicantId, refreshKey]);

    return status;
}

// Normalizes the view's TransferCreditStatus string to a tile state
// (case/whitespace-insensitive).
function mapTransferCreditsStatus(raw: string | null | undefined): TileStatus {
    switch ((raw ?? "").trim().toLowerCase()) {
        case "in progress":
            return "pending";
        case "not started":
        default:
            return "notStarted";
    }
}

// Enrollment Information ("Program Enrollment") section status for the dashboard
// "Your Next Steps" row. Sourced from Applicant.DashboardStatus_VW
// (ApplicantDashboardStatusVw.ProgramEnrollmentStatus), which resolves to one of:
//   "Not started" => the applicant has no enrollment yet (Applicant.ApplicantEnrollmentId is null)
//   "In Progress" => an enrollment exists but isn't complete (ApplicantEnrollment.DateCompleted null)
//   "Completed"   => the enrollment's DateCompleted is set
// Those map to the row's notStarted / pending / complete states.
// Not part of useDynamicTileStatuses' tile map — Enrollment Information renders in the
// Next Steps block, not as a tile — so Dashboard calls this hook directly.
export function useEnrollmentInformationStatus(user: User | null, refreshKey: string | number = 0): TileStatus {
    const [status, setStatus] = useState<TileStatus>("notStarted");

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!user?.applicantId) return;

            try {
                const raw = await enrollmentApi.getEnrollmentInformationStatus(user.applicantId);
                if (cancelled) return;
                setStatus(mapProgramEnrollmentStatus(raw));
            } catch {
                // No enrollment yet / lookup failed — leave the row in its default state.
                if (!cancelled) setStatus("notStarted");
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [user?.applicantId, refreshKey]);

    return status;
}

// Normalizes the view's ProgramEnrollmentStatus string to a tile state
// (case/whitespace-insensitive).
function mapProgramEnrollmentStatus(raw: string | null | undefined): TileStatus {
    switch ((raw ?? "").trim().toLowerCase()) {
        case "completed":
            return "complete";
        case "in progress":
            return "pending";
        case "not started":
        default:
            return "notStarted";
    }
}

// Enrollment Fees tile status, derived from the ApplicantEnrollmentFee records.
// "complete" once every fee tied to the applicant's enrollment is Paid or Waived
// (the API's isPaid flag = FeeStatusId != Pending, which covers Paid/Waived/PayPal/SensePass).
// Hidden until the enrollment has fee rows; fail-closed on load and on error.
function useFeesStatus(user: User | null, refreshKey: string | number = 0): DynamicTileState {
    const [state, setState] = useState<DynamicTileState>({ status: "notStarted", hidden: true });

    useEffect(() => {
        let cancelled = false;
        const noFees: DynamicTileState = { status: "notStarted", hidden: true };

        const load = async () => {
            if (!user?.applicantId) return;

            try {
                const applicantEnrollmentId = await enrollmentApi.getApplicantEnrollmentId(user.applicantId);
                if (!applicantEnrollmentId) {
                    if (!cancelled) setState(noFees);
                    return;
                }

                const fees = await enrollmentApi.getEnrollmentFees(applicantEnrollmentId);
                if (cancelled) return;

                if (!Array.isArray(fees) || fees.length === 0) {
                    setState(noFees);
                    return;
                }

                const allSettled = fees.every((f: any) => f?.isPaid === true);
                const nonePaid = fees.every((f: any) => f?.isPaid !== true);
                // All Paid/Waived => complete; all still Pending => not started; mixed => pending.
                setState({ status: allSettled ? "complete" : nonePaid ? "notStarted" : "pending" });
            } catch {
                // No enrollment yet / lookup failed — keep the tile hidden.
                if (!cancelled) setState(noFees);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [user?.applicantId, refreshKey]);

    return state;
}

// Housing Application tile
// Visibility: the tile only shows for applicants who answered "Yes" to "Do you require
// Student Housing?" in Program Enrollment (ApplicantHousing.RequireHousing, surfaced on
// the /api/HousingForm/Fetch response). That question is itself campus-gated, so a false/
// absent RequireHousing (campus offers no housing, or not yet answered) hides the tile.
// Fail-closed: default hidden and stay hidden on error, so the tile never surfaces to a
// student who isn't entitled to it.
// Status (when shown):
//   complete   => Housing page marked complete (server completedPages), OR the
//                 applicant is ineligible (a terminal outcome shown with neutral,
//                 non-discouraging wording per stakeholder guidance)
//   pending    => housing details panel complete but page not complete
//   notStarted => none of the above
// Mirrors the useFeesStatus async pattern so the tile survives across devices.
function useHousingStatus(user: User | null, refreshKey: string | number = 0): DynamicTileState {
    const [state, setState] = useState<DynamicTileState>({ status: "notStarted", hidden: true });

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!user?.applicantId) return;
            try {
                const [completed, housing] = await Promise.all([
                    dataApi.getCompletedSteps(),
                    housingApi.fetch<{ isEligible?: boolean | null; requireHousing?: boolean | null }>(),
                ]);
                if (cancelled) return;

                const hidden = housing?.requireHousing !== true;

                const completedSet = new Set(
                    (completed ?? []).map((id) => String(id).toLowerCase())
                );
                const pageComplete = completedSet.has(PAGE_IDS.housing.toLowerCase());
                const detailsComplete = completedSet.has(
                    PAGE_IDS.housingDetails.toLowerCase()
                );

                let status: TileStatus;
                if (pageComplete) {
                    status = "complete";
                } else if (housing?.isEligible === false) {
                    // Ineligibility is a terminal, completed outcome. Per stakeholder
                    // guidance we never surface negative/discouraging language on the
                    // dashboard, so this shows the neutral "complete" state. This also
                    // covers legacy ineligible applicants whose workflow-level Housing
                    // page GUID predates server-side finalization.
                    status = "complete";
                } else if (detailsComplete) {
                    status = "pending";
                } else {
                    status = "notStarted";
                }

                setState({ status, hidden });
            } catch {
                if (!cancelled) setState({ status: "notStarted", hidden: true });
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [user?.applicantId, refreshKey]);

    return state;
}

// Veteran/Military Benefits tile. The tile only shows for applicants who indicated
// military affiliation on the personal-profile military step (question 1 => the server's
// Applicant.IsActiveDutyRetired column, surfaced here as militaryInfo.MilitaryStatus by
// userMapper). Status is complete once every document the applicant's branch requires
// (COE for GI Bill, JST/LES depending on tuition-assistance path) has been uploaded; in
// progress once some but not all of those are; not started otherwise.
function useVeteranBenefitsStatus(user: User | null, refreshKey: string | number = 0): DynamicTileState {
    const [docsStatus, setDocsStatus] = useState<MilitaryDocumentsStatus>({});

    useEffect(() => {
        const applicantId = user?.applicantId;
        if (!applicantId) return;

        enrollmentApi
            .getMilitaryDocuments(applicantId)
            .then((docs) => setDocsStatus(docs ?? {}))
            .catch(() => {});
    }, [user?.applicantId, refreshKey]);

    const militaryInfo = getEffectiveMilitaryInfo(mapUserToForms(user ?? null).militaryInfo);
    const hidden = militaryInfo.MilitaryStatus !== "Yes";

    // Same rule as the Military & Veterans page: an unanswered status asks for no JST (it used to count as a service
    // member), and an applicant with nothing to upload shows complete instead of a tile that could never finish (UAT).
    const { statusKnown, required } = getMilitaryDocumentRequirements(militaryInfo);

    const uploadedCount = required.filter((docType) => Boolean(docsStatus[docType])).length;
    const status: TileStatus =
        statusKnown && required.length === 0
            ? "complete"
            : required.length === 0 || uploadedCount === 0
                ? "notStarted"
                : uploadedCount === required.length
                    ? "complete"
                    : "pending";

    return { status, hidden };
}

// Technology Check tile status. Unlike Military's three slots, Tech Check is a single
// upload (speed-test screenshot) submitted together with a yes/no requirements-met radio
// in one step (see TechnologyCheck.tsx / AuthController.SubmitTechCheck), so there's no
// meaningful "in progress" state - it's either uploaded or it isn't.
function useTechnologyCheckStatus(user: User | null, refreshKey: string | number = 0): TileStatus {
    const [docStatus, setDocStatus] = useState<DocumentUploadStatus | null>(null);

    useEffect(() => {
        const applicantId = user?.applicantId;
        if (!applicantId) return;

        enrollmentApi
            .getTechCheckStatus(applicantId)
            .then((status) => setDocStatus(status ?? null))
            .catch(() => {});
    }, [user?.applicantId, refreshKey]);

    return docStatus ? "complete" : "notStarted";
}

// Financial Assistance tile status. Primarily sourced from the server-side Applicant.DashboardStatus_VW
// (ApplicantDashboardStatusVw.FinancialAssistanceStatus), which aggregates the applicant's
// financial-planning progress into one of: "Not started", "In Progress", "Completed;Locked",
// or "Completed;NotLocked". Those map to the tile's notStarted / pending / complete states.
// Both "Completed" variants show the same completed status; "Completed;Locked" additionally
// disables the tile so the user can no longer navigate into the Financial workflow.
//
// Because that Reach-backed status doesn't update instantaneously as the user moves through the
// Financial workflow, we also read whether the applicant has completed any Financial panel pages
// (CompletedPage rows). When Reach still reports "Not started" but the user has started panels, we
// surface a client-derived "started" status (visually identical to "In Progress") so their progress
// is acknowledged. Any non-"Not started" Reach status is the more accurate source and takes
// precedence over the completed-page signal.
function useFinancialAssistanceStatus(user: User | null, refreshKey: string | number = 0): DynamicTileState {
    const [state, setState] = useState<DynamicTileState>({ status: "notStarted" });

    useEffect(() => {
        let cancelled = false;

        const applicantId = user?.applicantId;
        if (!applicantId) return;

        Promise.all([
            enrollmentApi.getFinancialAssistanceStatus(applicantId).catch(() => null),
            enrollmentApi.getFinancialPanelsProgress(applicantId).catch(() => ({ hasStarted: false, currentActionsComplete: false })),
        ])
            .then(([raw, progress]) => {
                if (cancelled) return;

                const reachState = mapFinancialAssistanceStatus(raw);

                // Reach only reports "Not started" but the user has completed at least one
                // Financial panel -> acknowledge their progress with a "started" status, or "awaiting" (Pending) once
                // every step the student can do now is done (it stayed In Progress with nothing left to do; UAT).
                if (reachState.status === "notStarted" && progress.hasStarted) {
                    setState({ status: progress.currentActionsComplete ? "awaiting" : "started" });
                    return;
                }

                setState(reachState);
            })
            .catch(() => {
                // Lookup failed - leave the tile in its default state.
                if (!cancelled) setState({ status: "notStarted" });
            });

        return () => {
            cancelled = true;
        };
    }, [user?.applicantId, refreshKey]);

    return state;
}

// Normalizes the view's status string to a tile state (case/whitespace-insensitive).
// The "Completed" value carries an optional ";Locked" / ";NotLocked" suffix indicating whether
// the applicant may still navigate into the workflow.
function mapFinancialAssistanceStatus(raw: string | null | undefined): DynamicTileState {
    const normalized = (raw ?? "").trim().toLowerCase();

    if (normalized.startsWith("completed")) {
        return { status: "complete", disabled: normalized === "completed;locked" };
    }

    switch (normalized) {
        case "in progress":
            return { status: "pending" };
        case "not started":
        default:
            return { status: "notStarted" };
    }
}